import React from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import {
  SectionCard as StyledSectionCard,
  SectionHeader,
  SectionTitle,
  SectionSummary,
  CollapseIcon,
  SectionBody,
  Chip,
  RemoveButton,
  FormGroup,
  Label,
  Input,
  Button,
  Select,
  Textarea,
  VariableChip
} from './StyledComponents';

const getSectionTypeLabel = (type) => {
  const labels = {
    anchor: 'For Each Anchor',
    lowestScore: 'For Each Lowest Score',
    highestScore: 'For Each Highest Score',
    biggestPositiveChange: 'For Each Biggest Positive Change',
    biggestNegativeChange: 'For Each Biggest Negative Change',
    factorScoreByHighest: 'For Each Factor Score by Highest',
    factorDistributionHighest: 'For Each Factor Distribution Scores by Highest',
    factorDistributionLowest: 'For Each Factor Distribution Scores by Lowest',
    outcomeForEach: 'Outcome (For Each)',
    outcomeInsight: 'Outcome Insight',
    overview_LLM: 'Overview LLM'
  };
  return labels[type] || type;
};

export const SectionCard = ({
  sectionName,
  section,
  isOpen,
  onToggle,
  onRemove,
  onAddLLMAction,
  onUpdateConfig,
  onUpdateLLMAction,
  onRemoveLLMAction,
  parentSectionName = null,
  showToastMessage,
  validateLLMActionName
}) => {
  const core_data = useSelector((state) => state.audit?.core_data, shallowEqual);
  const outcomeSections = core_data?.outcomeQuestions?.questions || [];
  const handleRemove = () => {
    onRemove(sectionName, parentSectionName);
  };

  const handleAddLLMAction = () => {
    onAddLLMAction(sectionName, parentSectionName);
  };

  const handleUpdateConfig = (field, value) => {
    onUpdateConfig(sectionName, field, value, parentSectionName);
  };

  const handleUpdateLLMAction = (actionId, field, value) => {
    onUpdateLLMAction(sectionName, actionId, field, value, parentSectionName);
  };

  const handleRemoveLLMAction = (actionId) => {
    onRemoveLLMAction(sectionName, actionId, parentSectionName);
  };

  const getSummaryChips = () => {
    const chips = [];
    
    if (section.llmActions && section.llmActions.length > 0) {
      chips.push(
        <Chip key="llm-actions" title={`${section.llmActions.length} LLM Actions`}>
          {section.llmActions.length} LLM
        </Chip>
      );
    }
    
    if (section.nestedSections && Object.keys(section.nestedSections).length > 0) {
      chips.push(
        <Chip key="nested-sections" title={`${Object.keys(section.nestedSections).length} Nested Sections`}>
          {Object.keys(section.nestedSections).length} Nested
        </Chip>
      );
    }
    
    return chips;
  };

  return (
    <StyledSectionCard>
      <SectionHeader onClick={onToggle}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <SectionTitle>{sectionName} - {getSectionTypeLabel(section.type)}</SectionTitle>
          <SectionSummary>
            {getSummaryChips()}
          </SectionSummary>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CollapseIcon open={isOpen}>▼</CollapseIcon>
          <RemoveButton onClick={(e) => {
            e.stopPropagation();
            handleRemove();
          }}>Remove</RemoveButton>
        </div>
      </SectionHeader>
      
      {isOpen && (
        <SectionBody>
          {/* Section Configuration */}
          {(section.type === 'lowestScore' || section.type === 'highestScore' || section.type === 'factorDistributionHighest' || section.type === 'factorDistributionLowest') && (
            <FormGroup>
              <Label>Number of items (default: 3)</Label>
              <Input
                type="number"
                value={section.config.num || 3}
                onChange={(e) => handleUpdateConfig('num', parseInt(e.target.value) || 3)}
                min="1"
                max="10"
              />
            </FormGroup>
          )}

          {section.type === 'anchor' && (
            <FormGroup>
              <Label>
                <input
                  type="checkbox"
                  checked={section.config.includeOutcomeScores || false}
                  onChange={(e) => handleUpdateConfig('includeOutcomeScores', e.target.checked)}
                />
                {' '}Include outcome scores
              </Label>
            </FormGroup>
          )}

          {section.type === 'outcomeInsight' && (
            <FormGroup>
              <Label>Outcome</Label>
              <Select
                value={section.config.outcomeId || ''}
                onChange={(e) => handleUpdateConfig('outcomeId', e.target.value)}
              >
                <option value="">Select an outcome</option>
                {outcomeSections.map(out => (
                  <option key={out.id} value={String(out.id)}>
                    {out.name || `Outcome ${out.id}`}
                  </option>
                ))}
              </Select>
            </FormGroup>
          )}

          {section.type === 'outcomeForEach' && (
            <FormGroup>
              <Label>
                <input
                  type="checkbox"
                  checked={section.config.includeFactorCorrelations || false}
                  onChange={(e) => handleUpdateConfig('includeFactorCorrelations', e.target.checked)}
                />
                {' '}Include correlations to factors
              </Label>
            </FormGroup>
          )}

          {/* LLM Actions */}
          <div style={{ marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <Label style={{ margin: 0 }}>LLM Actions</Label>
              <Button onClick={handleAddLLMAction}>Add LLM Action</Button>
            </div>

            {section.llmActions && section.llmActions.map(action => (
              <LLMActionCard
                key={action.id}
                action={action}
                onUpdate={handleUpdateLLMAction}
                onRemove={handleRemoveLLMAction}
                showToastMessage={showToastMessage}
                validateLLMActionName={validateLLMActionName}
                sectionType={section.type}
              />
            ))}
          </div>
        </SectionBody>
      )}
    </StyledSectionCard>
  );
};

// LLM Action Card Component
const LLMActionCard = ({ action, onUpdate, onRemove, showToastMessage, validateLLMActionName, sectionType }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const handleUpdate = (field, value) => {
    onUpdate(action.id, field, value);
  };

  const handleRemove = () => {
    onRemove(action.id);
  };

  const handleNameBlur = () => {
    const error = validateLLMActionName(action.name);
    if (error) {
      showToastMessage(error, 'error');
    }
  };

  const handleVariableClick = (variable) => {
    const currentPrompt = action.prompt || '';
    handleUpdate('prompt', currentPrompt + variable);
  };

  return (
    <StyledSectionCard style={{ marginBottom: '12px' }}>
      <SectionHeader onClick={() => setIsExpanded(!isExpanded)}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <SectionTitle>LLM Action</SectionTitle>
          <SectionSummary>
            <Chip>{action.name || 'Unnamed'}</Chip>
          </SectionSummary>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CollapseIcon open={isExpanded}>▼</CollapseIcon>
          <RemoveButton onClick={(e) => {
            e.stopPropagation();
            handleRemove();
          }}>Remove</RemoveButton>
        </div>
      </SectionHeader>
      
      {isExpanded && (
        <SectionBody>
          <FormGroup>
            <Label>Action Name (Key)</Label>
            <Input
              type="text"
              value={action.name}
              onChange={(e) => handleUpdate('name', e.target.value)}
              onBlur={handleNameBlur}
              placeholder="Enter a unique name for this action (used as key)"
            />
          </FormGroup>

          <FormGroup>
            <Label>Feedback Type</Label>
            <Select
              value={action.feedbackType}
              onChange={(e) => handleUpdate('feedbackType', e.target.value)}
            >
              <option value="current">Current Feedback</option>
              <option value="last">Last Feedback</option>
              <option value="both">Both Current and Last</option>
            {sectionType === 'outcomeForEach' && (
                <option value="highestDriverCorrelation">Highest Category Correlation</option>
            )}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Prompt Template</Label>
            <div style={{ marginBottom: '8px' }}>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                Available variables to inject:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
                <VariableChip onClick={() => handleVariableClick('{{feedback}}')}>
                  {'{{feedback}}'}
                </VariableChip>
                <VariableChip onClick={() => handleVariableClick('{{scores}}')}>
                  {'{{scores}}'}
                </VariableChip>
                <VariableChip onClick={() => handleVariableClick('{{factorNames}}')}>
                  {'{{factorNames}}'}
                </VariableChip>
                <VariableChip onClick={() => handleVariableClick('{{name}}')}>
                  {'{{name}}'}
                </VariableChip>
                <VariableChip onClick={() => handleVariableClick('{{change}}')}>
                  {'{{change}}'}
                </VariableChip>
                <VariableChip onClick={() => handleVariableClick('{{benchmark}}')}>
                  {'{{benchmark}}'}
                </VariableChip>
                <VariableChip onClick={() => handleVariableClick('{{outcomeScores}}')}>
                  {'{{outcomeScores}}'}
                </VariableChip>
              </div>
            </div>
            <Textarea
              value={action.prompt}
              onChange={(e) => handleUpdate('prompt', e.target.value)}
              placeholder="Enter your prompt template. Click variables above to inject them, or type {{variableName}} manually."
              style={{ minHeight: '220px' }}
            />
          </FormGroup>

          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={action.enabled}
                onChange={(e) => handleUpdate('enabled', e.target.checked)}
              />
              {' '}Enable this action
            </Label>
          </FormGroup>

          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={action.formatAsJson || false}
                onChange={(e) => handleUpdate('formatAsJson', e.target.checked)}
              />
              {' '}Format output as JSON
            </Label>
          </FormGroup>
        </SectionBody>
      )}
    </StyledSectionCard>
  );
}; 